/* eslint-disable camelcase, react/prop-types, max-lines, max-statements */
import React, {useEffect, useState, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Container, Card, Grid, Dropdown} from 'semantic-ui-react';
import {Actions} from '../../../common/actions';
import {uniqBy} from 'lodash';
import WtsMonthlyActivitiesTechDetailsModal from './registroTrabajoAgenciasTechDetails';
import {hasAccessPermission} from './../../../common/permissions/validations';
import {resources, scopes} from '../../../common/permissions/dictionary';
import WtsMonthlyActivitiesChart from './WtsMonthlyActivitiesChart/WtsMonthlyActivitiesChart';

import {
  MONTHS,
  REAL_YEAR,
  SERVICE_TYPES,
  monthTotal,
  checkEditable,
  getProcessedRecords,
  styles,
} from './wtsMonthlyActivities.helpers';

import {SafeIcon, ThWithSort, ThFilter} from './wtsMonthlyActivities.components';

const {
  getWtsAreas,
  changeWtsAreaSelected,
  getTrabajoAgencias,
  createTrabajoAgencia,
  updateTrabajoAgencia,
  getWtsAgenciesDealer,
  getWtsDealer,
} = Actions;

const WtsMonthlyActivities = () => {
  const dispatch = useDispatch();
  
  const permissions = useSelector((state) => state.auth?.permissions || state.session?.permissions || []);
  const currentUser = useSelector((state) => state.session || {});
  const {areas, areaSelected} = useSelector((state) => state.wtsAreas);
  const {trabajoAgencias} = useSelector((state) => state.wtsTrabajoAgencias);
  const wtsDealers = useSelector((state) => state.wtsDealer?.dealers || state.wtsDealer?.data || []);
  const rawAgenciesState = useSelector((state) => state.wtsAgencies);
  const wtsAgencies = rawAgenciesState?.agencies || rawAgenciesState?.data || [];

  const [filters, setFilters] = useState({'consecionario': '', 'agencia': '', 'year': REAL_YEAR});
  const [colFilters, setColFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({'key': null, 'dir': 'asc'});
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [yearOffset, setYearOffset] = useState(0);

  const canChangeDealer = useMemo(() => {
    return hasAccessPermission(permissions, resources.WtsRegistroTrabajoAgencias, scopes.Edit) ||
           hasAccessPermission(permissions, resources.WtsRegistroTrabajoAgencias, scopes.Create);
  }, [permissions]);

  const userDealerRuc = useMemo(() => {
    const ruc = String(currentUser.dealerId || currentUser.ruc || '');
    if (ruc) {
      return ruc;
    }
    const firstActive = wtsDealers.find((d) => d.status === 'ACTIVO' || d.status === 'A');
    return firstActive ? String(firstActive.ruc) : '';
  }, [currentUser, wtsDealers]);

  useEffect(() => {
    dispatch(getWtsAreas());
    if (typeof getWtsDealer === 'function') {
      dispatch(getWtsDealer());
    }
  }, [dispatch]);

  useEffect(() => {
    if (areaSelected?.id) {
      dispatch(getTrabajoAgencias(areaSelected.id));
    }
  }, [dispatch, areaSelected]);

  useEffect(() => {
    if (!filters.consecionario && userDealerRuc) {
      setFilters((prev) => ({...prev, 'consecionario': userDealerRuc}));
      if (areaSelected?.id) {
        dispatch(getWtsAgenciesDealer(areaSelected.id, userDealerRuc));
      }
    } else if (canChangeDealer && trabajoAgencias.length > 0 && !filters.consecionario) {
      const first = trabajoAgencias[0];
      const rucStr = String(first.consecionario || '');
      setFilters((prev) => ({
        ...prev,
        'consecionario': rucStr,
        'agencia': String(first.agencia || ''),
      }));
      if (areaSelected?.id && rucStr) {
        dispatch(getWtsAgenciesDealer(areaSelected.id, rucStr));
      }
    }
  }, [trabajoAgencias, filters.consecionario, areaSelected, dispatch, canChangeDealer, userDealerRuc]);

  const dealerOptions = useMemo(() => {
    let opts = (wtsDealers || [])
      .filter((d) => !d.status || d.status === 'ACTIVO' || d.status === 'A')
      .map((d) => ({
        'key': String(d.ruc),
        'value': String(d.ruc),
        'text': d.description || d.nombre || String(d.ruc),
      }));

    if (!canChangeDealer && userDealerRuc) {
      opts = opts.filter((o) => o.value === userDealerRuc);
    }
    if (filters.consecionario && !opts.find((o) => o.value === String(filters.consecionario))) {
      opts.push({
        'key': String(filters.consecionario),
        'value': String(filters.consecionario),
        'text': String(filters.consecionario),
      });
    }
    return opts;
  }, [wtsDealers, filters.consecionario, canChangeDealer, userDealerRuc]);

  const agencyOptions = useMemo(() => {
    const opts = uniqBy(wtsAgencies || [], 'id')
      .filter((a) => !a.status || a.status === 'ACTIVO' || a.status === 'A')
      .map((a) => ({
        'key': String(a.id),
        'value': String(a.id),
        'text': a.agency || a.agencia || a.description || String(a.id),
      }));
    if (filters.agencia && !opts.find((o) => o.value === String(filters.agencia))) {
      opts.push({
        'key': String(filters.agencia),
        'value': String(filters.agencia),
        'text': String(filters.agencia),
      });
    }
    return opts;
  }, [wtsAgencies, filters.agencia]);

  const currentYearOptions = useMemo(() => {
    return Array.from({'length': 11}, (_, i) => {
      const y = REAL_YEAR - 5 + i + yearOffset;
      return {'key': y, 'value': y, 'text': String(y)};
    });
  }, [yearOffset]);

  const processedRecords = useMemo(() => {
    return getProcessedRecords(trabajoAgencias, filters, colFilters, sortConfig);
  }, [trabajoAgencias, filters, colFilters, sortConfig]);

  const hasDataForChart = useMemo(() => {
    if (!processedRecords || processedRecords.length === 0) {
      return false;
    }
    return processedRecords.some((rec) => monthTotal(rec) > 0);
  }, [processedRecords]);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {'key': prev.dir === 'asc' ? key : null, 'dir': prev.dir === 'asc' ? 'desc' : 'asc'};
      }
      return {'key': key, 'dir': 'asc'};
    });
  };

  const handleFilterChange = (key, val) => {
    setColFilters((prev) => ({...prev, [key]: val}));
  };

  const handleFilterDealerChange = (value) => {
    const strVal = String(value);
    setFilters({...filters, 'consecionario': strVal, 'agencia': ''});
    setEditingId(null);
    if (areaSelected?.id) {
      dispatch(getWtsAgenciesDealer(areaSelected.id, strVal));
    }
  };

  const handleSaveEdit = () => {
    if (!editValues.id_tipo_trabajo) {
      return;
    }
    const monthsPayload = {};
    MONTHS.forEach((m) => {
      monthsPayload[m.key] = parseInt(editValues[m.key], 10) || 0;
    });

    let action, payload;

    if (editValues.id) {
      payload = {
        ...editValues,
        ...monthsPayload,
        'id_tipo_trabajo': Number(editValues.id_tipo_trabajo),
      };
      action = updateTrabajoAgencia(payload.id, payload);
    } else {
      payload = {
        ...monthsPayload,
        'anio': filters.year,
        'consecionario': filters.consecionario,
        'agencia': filters.agencia,
        'idarea': areaSelected.id,
        'id_tipo_trabajo': Number(editValues.id_tipo_trabajo),
      };
      action = createTrabajoAgencia(payload);
    }

    dispatch(action).then(() => {
      setEditingId(null);
    });
  };

  const handleEditClick = (rec) => {
    setEditingId(rec.id_tipo_trabajo);
    setEditValues({...rec, 'id_tipo_trabajo': String(rec.id_tipo_trabajo)});
  };

  return (
    <>
      <style>{styles}</style>
      <Container fluid className="wma-root">
        <Card
          fluid
          style={{
            'borderRadius': 12,
            'border': 'none',
            'boxShadow': '0 4px 20px rgba(0,0,0,0.05)',
          }}
        >
          <div className="wma-title-bar">
            <div style={{'display': 'flex', 'alignItems': 'center', 'marginBottom': '14px'}}>
              <h2 className="wma-title" style={{'margin': 0, 'marginRight': '10px'}}>
                Registro Tareas Periodo
              </h2>
              <WtsMonthlyActivitiesTechDetailsModal />
            </div>
            <Grid>
              <Grid.Column width={6}>
                <div className="wma-area-label">Seleccionar Área</div>
                <Dropdown
                  fluid selection
                  placeholder="Seleccione un área"
                  value={areaSelected?.id}
                  options={(areas || []).map((a) => ({
                    'key': a.id,
                    'value': a.id,
                    'text': a.description,
                  }))}
                  onChange={(e, {value}) => {
                    const sel = areas.find((a) => a.id === value);
                    dispatch(changeWtsAreaSelected(sel));
                  }}
                />
              </Grid.Column>
            </Grid>
          </div>
          <div className="wma-content-card">
            <div className="wma-toolbar">
              <Grid>
                <Grid.Row verticalAlign="middle">
                  <Grid.Column width={5}>
                    <label className="wma-label">Concesionario</label>
                    <Dropdown
                      fluid selection search
                      disabled={!canChangeDealer}
                      placeholder="Seleccione..."
                      value={String(filters.consecionario || '')}
                      options={dealerOptions}
                      onChange={(e, {value}) => handleFilterDealerChange(value)}
                    />
                  </Grid.Column>
                  <Grid.Column width={1} />
                  <Grid.Column width={5}>
                    <label className="wma-label">Agencia</label>
                    <Dropdown
                      fluid selection search
                      disabled={!filters.consecionario}
                      placeholder="Seleccione..."
                      value={String(filters.agencia || '')}
                      options={agencyOptions}
                      onChange={(e, {value}) => {
                        setFilters({...filters, 'agencia': String(value)});
                        setEditingId(null);
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              <div className="wma-toolbar-bottom">
                <div className="wma-year-nav-wrap">
                  <button
                    className="wma-year-nav-btn"
                    onClick={() => setYearOffset((prev) => prev - 1)}
                  >
                    &#10094;
                  </button>
                  <div className="wma-year-tabs" style={{'flex': 1}}>
                    {currentYearOptions.map((y) => (
                      <button
                        key={y.value}
                        className={`wma-year-tab ${y.value === filters.year ? 'active' : ''}`}
                        onClick={() => {
                          setFilters({...filters, 'year': y.value});
                          setEditingId(null);
                        }}
                      >
                        {y.value}
                        {y.value === REAL_YEAR && <span className="wma-star">★</span>}
                      </button>
                    ))}
                  </div>
                  <button
                    className="wma-year-nav-btn"
                    onClick={() => setYearOffset((prev) => prev + 1)}
                  >
                    &#10095;
                  </button>
                </div>
              </div>
            </div>
            <div className="wma-table-wrap">
              <table className="wma-table">
                <thead>
                  <tr className="header-row">
                    <ThWithSort
                      label="Tipo de Trabajo"
                      sortKey="id_tipo_trabajo"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                    {MONTHS.map((m) => (
                      <ThWithSort
                        key={m.key}
                        label={m.label}
                        sortKey={m.key}
                        sortConfig={sortConfig}
                        onSort={requestSort}
                      />
                    ))}
                    <th>Total</th>
                    <th className="sticky-col">Acciones</th>
                  </tr>
                  <tr>
                    <ThFilter
                      colKey="id_tipo_trabajo"
                      filters={colFilters}
                      onChange={handleFilterChange}
                    />
                    {MONTHS.map((m) => (
                      <ThFilter
                        key={m.key}
                        colKey={m.key}
                        filters={colFilters}
                        onChange={handleFilterChange}
                      />
                    ))}
                    <th style={{'background': '#d4edf8'}}></th>
                    <th className="sticky-col" style={{'background': '#d4edf8'}}></th>
                  </tr>
                </thead>
                <tbody>
                  {processedRecords.map((rec) => {
                    const isEditing = editingId === rec.id_tipo_trabajo;
                    const recTotal = monthTotal(isEditing ? editValues : rec);
                    const serviceObj = SERVICE_TYPES.find(
                      (s) => String(s.id) === String(rec.id_tipo_trabajo)
                    );
                    const serviceName = serviceObj ? serviceObj.label : rec.id_tipo_trabajo;
                    
                    return (
                      <tr key={rec.id_tipo_trabajo}>
                        <td>{serviceName}</td>
                        {MONTHS.map((m) => (
                          <td key={m.key} style={{'textAlign': 'center'}}>
                            {isEditing ? (
                              <input
                                type="number"
                                className="wma-month-input"
                                value={editValues[m.key]}
                                disabled={!checkEditable(m.key, filters.year)}
                                onChange={(e) => setEditValues({
                                  ...editValues,
                                  [m.key]: e.target.value,
                                })}
                              />
                            ) : (
                              <span className="wma-month-text" onDoubleClick={() => handleEditClick(rec)}>
                                {rec[m.key] || 0}
                              </span>
                            )}
                          </td>
                        ))}
                        <td style={{'fontWeight': 'bold', 'textAlign': 'center'}}>
                          {recTotal}
                        </td>
                        <td className="sticky-col">
                          {isEditing ? (
                            <>
                              <button
                                title="Guardar"
                                className="wma-action-btn"
                                onClick={handleSaveEdit}
                                disabled={!editValues.id_tipo_trabajo}
                              >
                                <SafeIcon name="save" color="#23b563" size="1.2em" />
                              </button>
                              <button
                                title="Cancelar"
                                className="wma-action-btn"
                                style={{'marginLeft': '8px'}}
                                onClick={() => setEditingId(null)}
                              >
                                <SafeIcon name="cancel" color="#db2828" size="1.2em" />
                              </button>
                            </>
                          ) : (
                            <button
                              title="Editar"
                              className="wma-action-btn"
                              onClick={() => handleEditClick(rec)}
                            >
                              <SafeIcon name="edit" color="#58B8E6" size="1.1em" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasDataForChart && (
              <div className="wma-chart-container">
                <WtsMonthlyActivitiesChart
                  records={processedRecords}
                  serviceTypes={SERVICE_TYPES}
                  year={filters.year}
                />
              </div>
            )}
          </div>
        </Card>
      </Container>
    </>
  );
};

export default WtsMonthlyActivities;
